import { arrayOfAll } from '#src/app/shared/utils/arrays';

import type { Category, Question, QuestionSelect, ReportType, SubCategory } from '../types';

export const CATEGORIES = arrayOfAll<Category>()(['violates_guidelines', 'illegal_content', 'dislike_content']);

// This allows for questions because there are situations where instead of having a child, the field immediately has questions
type CategoryMapping = Readonly<Record<Category, QuestionSelect>>;
type SubCategoryMapping = Readonly<Record<SubCategory, QuestionSelect>>;

const contentViolationQuestion: Question = { type: 'text', name: 'content_violation', targetField: 'description' };

const CATEGORIES_MAPPING_DEFAULTS: CategoryMapping = {
	violates_guidelines: {
		type: 'sub_category',
		value: [
			'bullying_harassment',
			'hate_discrimination',
			'defamation_libel',
			'violence',
			'selfharm_suicide',
			'graphic_imagery',
			'privacy_violations',
			'minors_protection',
			'pornography',
			'misleading_information',
			'deception_fraud',
			'excessive_edits',
			'spam',
			'intellectual_property',
		],
	},
	illegal_content: {
		type: 'sub_category',
		value: [
			'defamation_libel',
			'violence',
			'minors_protection',
			'deception_fraud',
			'intellectual_property',
			'other_illegal',
		],
	},
	dislike_content: {
		type: 'question',
		value: [{ type: 'text', name: 'additional_information', targetField: 'description' }],
	},
};

const SUB_CATEGORIES_MAPPING_DEFAULTS: SubCategoryMapping = {
	bullying_harassment: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'identify_target',
				options: ['self', 'acquaintance', 'third_person', 'minor_child', 'legal_representative'],
				targetField: 'targeting',
				required: true,
			},
			contentViolationQuestion,
		],
	},
	hate_discrimination: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'identify_target',
				options: ['protected_class', 'self', 'minor_child', 'legal_representative', 'hate_group', 'none_above'],
				targetField: 'targeting',
				required: true,
			},
			contentViolationQuestion,
		],
	},
	defamation_libel: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'defamation_target',
				options: ['self', 'acquaintance', 'third_person', 'minor_child', 'legal_representative', 'none_above'],
				targetField: 'targeting',
				required: true,
			},
			{ type: 'text', name: 'problematic_information', targetField: 'description', required: true },
		],
	},
	violence: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'select',
				options: ['potential_violence', 'terrorism'],
				targetField: 'abuseReason',
			},
			contentViolationQuestion,
		],
	},
	selfharm_suicide: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'select',
				options: ['self_harm', 'suicide'],
				targetField: 'abuseReason',
			},
			contentViolationQuestion,
		],
	},
	graphic_imagery: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'select',
				options: ['photo', 'video'],
				targetField: 'abuseReason',
			},
			contentViolationQuestion,
		],
	},
	privacy_violations: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'exposure_target',
				options: ['self', 'legal_representative', 'minor_child', 'none_above'],
				targetField: 'targeting',
				required: true,
			},
			contentViolationQuestion,
		],
	},
	minors_protection: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'select',
				options: ['privacy', 'csam', 'age_restrictions'],
				targetField: 'abuseReason',
			},
			{
				type: 'options',
				name: 'minor_relation',
				options: ['self', 'parent_legal', 'unknown_minor'],
				targetField: 'minorRelation',
				required: true,
			},
			contentViolationQuestion,
		],
	},
	pornography: { type: 'question', value: [{ type: 'text', name: 'policy_violation', targetField: 'description' }] },
	misleading_information: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'information_type',
				options: ['elections', 'public_health', 'current_events', 'private_dispute', 'conspiracy_theory', 'none_above'],
				targetField: 'misleadingInfoDetails',
			},
			{ type: 'text', name: 'problematic_information', targetField: 'description', required: true },
		],
	},
	deception_fraud: {
		type: 'question',
		value: [{ type: 'text', name: 'deception_details', required: true, targetField: 'description' }],
	},
	excessive_edits: {
		type: 'question',
		value: [
			{
				type: 'options',
				name: 'change_type',
				options: ['ask_changed', 'new_info'],
				targetField: 'abuseReason',
			},
			{
				type: 'text',
				name: 'describe_change',
				targetField: 'description',
			},
		],
	},
	spam: { type: 'question', value: [contentViolationQuestion] },
	intellectual_property: {
		type: 'question',
		value: [
			{
				type: 'link',
				name: 'submit_infringement',
				url: 'https://change.my.site.com/legalforms/s/copyright-and-trademark',
				queryParams: { Petition_Id: 'petitionId' },
			},
		],
	},
	other_illegal: {
		type: 'question',
		value: [
			{
				type: 'text',
				name: 'describe_illegal',
				required: true,
				targetField: 'description',
			},
		],
	},
};

export const CATEGORIES_MAPPING: Readonly<Record<ReportType, CategoryMapping>> = {
	petition: CATEGORIES_MAPPING_DEFAULTS,
};

export const SUB_CATEGORIES_MAPPING: Readonly<Record<ReportType, SubCategoryMapping>> = {
	petition: SUB_CATEGORIES_MAPPING_DEFAULTS,
};
